import { ChevronLeft, ChevronRight } from 'lucide-react';
import { periodOf } from '@outsource/shared/dates';
import { dateFullRu, monthNameRu } from '@outsource/shared/ru';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { today } from '../../lib/clock';
import { useLayer } from '../../lib/layers';
import { WEEKDAYS_RU, monthGrid, movedDay, shiftMonths, validDate } from './calendar';
import { Icon } from './Icon';

/** Gap between the popover and its input, and between the popover and the viewport edge. */
const ANCHOR_GAP = 4;
const VIEWPORT_MARGIN = 8;

export interface DatePopoverProps {
  /** The date input the popover belongs to: it is positioned under it and keeps focus on open. */
  readonly anchorRef: RefObject<HTMLInputElement | null>;
  /** The input's value: `YYYY-MM-DD`, '' or a partly typed date. */
  readonly value: string;
  readonly onPick: (date: string) => void;
  /** Shown while the input has a value; picks ''. */
  readonly canClear: boolean;
  readonly onClose: () => void;
}

/** Under the input, or above it when there is no room below; kept inside the viewport. */
function usePosition(anchorRef: RefObject<HTMLElement | null>, panelRef: RefObject<HTMLElement | null>) {
  const [position, setPosition] = useState<CSSProperties>({ top: 0, left: 0, visibility: 'hidden' });
  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      const panel = panelRef.current?.getBoundingClientRect();
      if (!anchor || !panel) return;
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const fitsBelow = anchor.bottom + ANCHOR_GAP + panel.height <= viewportHeight - VIEWPORT_MARGIN;
      const top = fitsBelow || anchor.top < panel.height ? anchor.bottom + ANCHOR_GAP : anchor.top - ANCHOR_GAP - panel.height;
      const left = Math.min(anchor.left, viewportWidth - panel.width - VIEWPORT_MARGIN);
      setPosition({ top, left: Math.max(VIEWPORT_MARGIN, left) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef, panelRef]);
  return position;
}

/**
 * Closes on a press outside the popover and its input. Pointer events, not mouse events: iPadOS
 * sends no mousedown for a tap on an element without a click handler.
 */
function useDismissOnOutside(
  panelRef: RefObject<HTMLElement | null>,
  anchorRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    const onPointerDown = (event: Event) => {
      const target = event.target;
      const isInside = target instanceof Node && (!!panelRef.current?.contains(target) || !!anchorRef.current?.contains(target));
      if (!isInside) onCloseRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [panelRef, anchorRef]);
}

const monthTitle = (period: string): string => {
  const name = monthNameRu(period);
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${period.slice(0, 4)}`;
};

const NAV_BUTTON =
  'grid size-7 cursor-pointer place-items-center rounded-ctl border-0 bg-transparent text-fg-2 hover:bg-neutral-100 hover:text-fg-1 focus-visible:outline-2 focus-visible:outline-pi-navy';
const FOOTER_BUTTON =
  'h-7 cursor-pointer rounded-ctl border-0 bg-transparent px-2 text-[13px] font-medium text-fg-1 hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-pi-navy';

interface DayButtonProps {
  readonly date: string;
  readonly period: string;
  readonly selected: string | null;
  readonly focused: string;
  readonly onPick: (date: string) => void;
}

function DayButton({ date, period, selected, focused, onPick }: DayButtonProps) {
  const isSelected = date === selected;
  return (
    <button
      type="button"
      data-date={date}
      tabIndex={date === focused ? 0 : -1}
      aria-label={dateFullRu(date)}
      aria-pressed={isSelected}
      aria-current={date === today() ? 'date' : undefined}
      onClick={() => onPick(date)}
      className={cn(
        'grid size-8 cursor-pointer place-items-center rounded-ctl border text-[13px] tabular-nums focus-visible:outline-2 focus-visible:outline-pi-navy',
        isSelected ? 'border-pi-navy bg-pi-navy font-semibold text-pi-paper' : 'bg-transparent hover:bg-neutral-100',
        !isSelected && (date === today() ? 'border-pi-navy' : 'border-transparent'),
        !isSelected && (periodOf(date) === period ? 'text-fg-1' : 'text-fg-3'),
      )}
    >
      {Number(date.slice(8, 10))}
    </button>
  );
}

/**
 * The app's own calendar for a date input (decisions-log V186): the native picker of iPadOS does
 * not take a tapped day. Portaled with fixed positioning so dialogs and grids never clip it; the
 * input keeps focus on open so typing still works, Tab enters the days, arrows / PageUp / PageDown /
 * Home / End move between them, Esc or a press outside closes it and focus returns to the input.
 */
export function DatePopover({ anchorRef, value, onPick, canClear, onClose }: DatePopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const selected = validDate(value);
  const [focused, setFocused] = useState(() => selected ?? today());
  const [period, setPeriod] = useState(() => periodOf(focused));
  const isKeyboardMove = useRef(false);
  const position = usePosition(anchorRef, panelRef);
  useLayer({ kind: 'dialog', panelRef, onClose, initialFocusRef: anchorRef });
  useDismissOnOutside(panelRef, anchorRef, onClose);

  useEffect(() => {
    if (!isKeyboardMove.current) return;
    isKeyboardMove.current = false;
    panelRef.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus();
  }, [focused]);

  const moveTo = (date: string) => {
    setFocused(date);
    setPeriod(periodOf(date));
  };
  const showMonth = (months: number) => moveTo(shiftMonths(focused, months));
  const onGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = movedDay(focused, event.key);
    if (next === null) return;
    // The grid owns these keys: they must not reach a DataGrid row behind the portal.
    event.preventDefault();
    event.stopPropagation();
    isKeyboardMove.current = true;
    moveTo(next);
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Календарь"
      onClick={(event) => event.stopPropagation()}
      className="fixed z-[58] flex w-[248px] flex-col gap-1 rounded-2 border border-border-1 bg-bg-3 p-2 text-[13px] text-fg-1 shadow-md"
      style={position}
    >
      <div className="flex items-center justify-between">
        <button type="button" aria-label="Предыдущий месяц" onClick={() => showMonth(-1)} className={NAV_BUTTON}>
          <Icon icon={ChevronLeft} size={16} />
        </button>
        <span aria-live="polite" className="font-semibold">
          {monthTitle(period)}
        </span>
        <button type="button" aria-label="Следующий месяц" onClick={() => showMonth(1)} className={NAV_BUTTON}>
          <Icon icon={ChevronRight} size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 justify-items-center text-[11px] font-medium text-fg-3" aria-hidden="true">
        {WEEKDAYS_RU.map((day) => (
          <span key={day} className="py-1">
            {day}
          </span>
        ))}
      </div>
      <div role="group" aria-label={monthTitle(period)} onKeyDown={onGridKeyDown} className="grid grid-cols-7 justify-items-center gap-y-0.5">
        {monthGrid(period).map((date) => (
          <DayButton key={date} date={date} period={period} selected={selected} focused={focused} onPick={onPick} />
        ))}
      </div>
      <div className="flex justify-between border-t border-border-1 pt-1">
        <button type="button" onClick={() => onPick(today())} className={FOOTER_BUTTON}>
          Сегодня
        </button>
        {canClear && (
          <button type="button" onClick={() => onPick('')} className={cn(FOOTER_BUTTON, 'text-fg-2')}>
            Очистить
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
